import { useState, useEffect, useRef } from 'react'
import { cloneDeep, isEmpty, debounce } from '@/utils/lodash'
import { useMutation, useQuery } from '@apollo/client/react'
import { SAVE_ASSUMPTIONS } from '@/housefolios-components/AssumptionsSettings/mutations'
import {
  SAVE_ANALYSIS,
  UPDATE_PROPERTY,
} from '@/housefolios-components/PropertyContainer/mutations'
import { UPDATE_LOT } from '../mutations'
import { GET_PROPERTY } from '@/housefolios-components/PropertyContainer/queries'
import { GET_LOT } from '../queries'
import renderInputFieldsFunction from '../../Functions/renderInput'
// Import all necessary queries and mutations here
// import { ... } from '...'

// Utility functions (move these above field arrays)
const isNil = (v) => v === null || v === undefined
const head = (arr) => (Array.isArray(arr) ? arr[0] : arr)
const get = (obj, path, def) => {
  try {
    return path.split('.').reduce((o, k) => (o || {})[k], obj) ?? def
  } catch {
    return def
  }
}
const sum = (arr) =>
  Array.isArray(arr) ? arr.reduce((a, b) => a + (b || 0), 0) : 0
const find = (arr, fn) =>
  Array.isArray(arr) ? arr.find(fn) : undefined
const concat = (...arrs) => arrs.flat()
const every = (arr, fn) => (Array.isArray(arr) ? arr.every(fn) : true)

// Import or define statesShort
import { STATES } from '@/constants/states'
const statesShort = () => STATES.map((s) => s.shortname)

const useEditLotForm = (props) => {
  // Move all state and effect logic from index.jsx here
  // Example:
  const [analysisInput, setAnalysisInput] = useState({})
  const [lotInput, setLotInput] = useState({})
  const [propertyInput, setPropertyInput] = useState({})
  const [assumptionsInput, setAssumptionsInput] = useState({})
  const [lotProcessesInput, setLotProcessesInput] = useState({})
  const [loader, setLoader] = useState(false)
  const [saveAssumptions] = useMutation(SAVE_ASSUMPTIONS)
  const [saveAnalysis] = useMutation(SAVE_ANALYSIS)
  const [updateLot] = useMutation(UPDATE_LOT)
  // ... other state as needed

  // Fetch lot and property data when open2 (lotId) is provided
  const lotId = props.open2
  console.log('[useEditLotForm] Querying GET_LOT with lotId:', lotId)
  const { data: lotData, loading: lotLoading } = useQuery(GET_LOT, {
    variables: { id: lotId },
    skip: !lotId,
  })
  useEffect(() => {
    console.log('[useEditLotForm] Raw lotData response:', lotData)
  }, [lotData])

  const propertyId = lotData?.lot?.propertyId
  console.log(
    '[useEditLotForm] Querying GET_PROPERTY with propertyId:',
    propertyId,
  )
  const { data: propertyData, loading: propertyLoading } = useQuery(
    GET_PROPERTY,
    {
      variables: { id: propertyId },
      skip: !propertyId,
    },
  )
  useEffect(() => {
    console.log(
      '[useEditLotForm] Raw propertyData response:',
      propertyData,
    )
  }, [propertyData])

  // Placeholder loading flags for future implementation
  const lotPortfolioLoading = false
  const lotProcessesLoading = false

  useEffect(() => {
    if (lotData && lotData.lot) {
      setLotInput(lotData.lot)
      // Set analysisInput if available
      if (lotData.lot.analysis) {
        // Ensure all mapped array fields are always arrays
        const analysis = {
          ...lotData.lot.analysis,
          b2rMezzanineOfferPrice: Array.isArray(
            lotData.lot.analysis.b2rMezzanineOfferPrice,
          )
            ? lotData.lot.analysis.b2rMezzanineOfferPrice
            : [0],
          rent: Array.isArray(lotData.lot.analysis.rent)
            ? lotData.lot.analysis.rent
            : [0],
          rentBOR: Array.isArray(lotData.lot.analysis.rentBOR)
            ? lotData.lot.analysis.rentBOR
            : [0],
        }
        setAnalysisInput(analysis)
        console.log(
          '[useEditLotForm] Set analysisInput from lotData.lot.analysis:',
          analysis,
        )
      } else if (propertyData && propertyData.property) {
        // Derive a minimal analysisInput from propertyData.property or lotData.lot
        const minimalAnalysis = {
          listPrice:
            lotData.lot.askingPrice ||
            propertyData.property.estval ||
            0,
          afterRepairValue: propertyData.property.estval || 0,
          b2rMezzanineOfferPrice: [0], // Always at least one
          rent: [0],
          rentBOR: [0],
          // Add more fields as needed
        }
        setAnalysisInput(minimalAnalysis)
        console.log(
          '[useEditLotForm] Set analysisInput from minimal derived:',
          minimalAnalysis,
        )
      }
      // Defensive for assumptionsInput (e.g., avgRate)
      if (lotData.lot.assumptions) {
        const assumptions = {
          ...lotData.lot.assumptions,
          avgRate: Array.isArray(lotData.lot.assumptions.avgRate)
            ? lotData.lot.assumptions.avgRate
            : [0],
        }
        setAssumptionsInput(assumptions)
      }
    }
  }, [lotData, propertyData])

  useEffect(() => {
    if (propertyData && propertyData.property) {
      console.log(
        '[useEditLotForm] Fetched propertyData:',
        propertyData,
      )
      setPropertyInput(propertyData.property)
    }
  }, [propertyData])

  // Example debounced submit logic
  const debouncedSubmitAnalysis = useRef(
    debounce((propertyId, analysisInput, source, propertyRefetch) => {
      // submitAnalysis logic here
    }, 1000),
  ).current

  // Example effect
  useEffect(() => {
    console.log(
      '[useEditLotForm] Hook initialized with props:',
      props,
    )
  }, [])

  // Example for each state
  useEffect(() => {
    console.log('[useEditLotForm] analysisInput:', analysisInput)
  }, [analysisInput])
  useEffect(() => {
    console.log('[useEditLotForm] lotInput:', lotInput)
  }, [lotInput])
  useEffect(() => {
    console.log('[useEditLotForm] propertyInput:', propertyInput)
  }, [propertyInput])
  useEffect(() => {
    console.log(
      '[useEditLotForm] assumptionsInput:',
      assumptionsInput,
    )
  }, [assumptionsInput])
  useEffect(() => {
    console.log(
      '[useEditLotForm] lotProcessesInput:',
      lotProcessesInput,
    )
  }, [lotProcessesInput])
  useEffect(() => {
    console.log('[useEditLotForm] loader:', loader)
  }, [loader])
  useEffect(() => {
    console.log('[useEditLotForm] propertyLoading:', propertyLoading)
  }, [propertyLoading])
  useEffect(() => {
    console.log(
      '[useEditLotForm] lotPortfolioLoading:',
      lotPortfolioLoading,
    )
  }, [lotPortfolioLoading])
  useEffect(() => {
    console.log('[useEditLotForm] lotLoading:', lotLoading)
  }, [lotLoading])
  useEffect(() => {
    console.log(
      '[useEditLotForm] lotProcessesLoading:',
      lotProcessesLoading,
    )
  }, [lotProcessesLoading])

  // Example handler
  const onChangeAnalysis = (event, source = null, quick = false) => {
    // ... handler logic
  }

  // Example guard for saveAssumptions
  const guardedSaveAssumptions = (
    propertyId,
    assumptionsInput,
    organizationId,
  ) => {
    if (!propertyId || !organizationId || !assumptionsInput) return
    saveAssumptions({
      variables: {
        documentId: propertyId,
        assumptionsInput: assumptionsInput,
        documentType: 'property',
      },
      refetchQueries: [
        {
          query: GET_PROPERTY,
          variables: { id: propertyId },
        },
      ],
    })
  }

  // Example guard for saveAnalysis
  const guardedSaveAnalysis = (
    propertyId,
    analysisInput,
    organizationId,
  ) => {
    if (!propertyId || !organizationId || !analysisInput) return
    saveAnalysis({
      variables: {
        propertyId: propertyId,
        analysisInput: analysisInput,
      },
      refetchQueries: [
        {
          query: GET_PROPERTY,
          variables: { id: propertyId },
        },
      ],
    })
  }

  // Example guard for updateLot
  const guardedUpdateLot = (lotId, lotInput, organizationId) => {
    if (!lotId || !organizationId || !lotInput) return
    updateLot({
      variables: {
        id: lotId,
        lotInput: lotInput,
      },
      refetchQueries: [
        {
          query: GET_LOT,
          variables: { id: lotId },
        },
      ],
    })
  }

  useEffect(() => {
    console.log('[useEditLotForm] Returning values:', {
      analysisInput,
      lotInput,
      propertyInput,
      assumptionsInput,
      lotProcessesInput,
      loader,
      propertyLoading,
      lotPortfolioLoading,
      lotLoading,
      lotProcessesLoading,
      // ...other handlers and state
    })
  })

  // FIELD ARRAYS MOVED FROM index.jsx
  const lotDetailFields = [
    {
      type: 'text',
      name: 'county',
      label: 'County',
      value: propertyInput?.county,
      xs: 12,
      details: true,
    },
    {
      type: 'text',
      name: 'address',
      label: 'Street Address',
      value: propertyInput?.address,
      xs: 12,
      details: true,
    },
    {
      type: 'text',
      name: 'city',
      label: 'City',
      value: propertyInput?.city,
      xs: 12,
      md: 6,
      details: true,
    },
    {
      type: 'autocomplete',
      name: 'state',
      label: 'State',
      value: propertyInput?.state,
      options: statesShort(),
      xs: 12,
      md: 6,
      details: true,
    },
    {
      type: 'number',
      name: 'zip',
      label: 'Zip',
      value: propertyInput?.zip,
      xs: 12,
      md: 6,
      details: true,
    },
    {
      type: 'text',
      name: 'subdivision',
      label: 'Subdivision',
      value: propertyInput?.subdivision,
      xs: 12,
      md: 6,
      details: true,
    },
    {
      type: 'dollar',
      name: 'askingPrice',
      label: 'Asking Price',
      value: isNil(lotInput?.askingPrice)
        ? ''
        : lotInput?.askingPrice?.toString(),
      xs: 12,
      md: 6,
      lot: true,
      tooltipField:
        'The price that the seller is asking for this home. *Not necessarily the price that the investor will purchase the home for.',
      tooltipButtonText: 'Asking Price Options',
    },
    {
      name: 'plan',
      value: propertyInput?.plan || '',
      label: 'Plan',
      options: props?.session?.me?.activeOrg?.plans || [],
      optionName: 'plans',
      editOption: true,
      removeOption: true,
      type: 'select',
      xs: 12,
      md: 6,
      details: true,
    },
  ]

  const lotLoanFields = [
    {
      name: 'lotLender',
      value: analysisInput?.lotLender || '',
      label: 'Lender',
      options: (props?.session?.me?.activeOrg?.lenders || []).filter(
        (lender) => lender.type?.includes('Lot'),
      ),
      optionName: 'lenders',
      removeOption: true,
      type: 'select',
      analysis: true,
      xs: 12,
      md: 6,
    },
    {
      type: 'dollar',
      name: 'b2rLotOfferPrice',
      label: 'Loan Amount',
      value: isNil(analysisInput?.b2rLotOfferPrice)
        ? ''
        : analysisInput?.b2rLotOfferPrice?.toString(),
      xs: 12,
      lg: 6,
      analysis: true,
      tooltipField:
        'The dollar amount of the loan meant for the lot.',
    },
    {
      type: 'date',
      name: 'lotStartDate',
      label: 'Start Date',
      value: assumptionsInput?.lotStartDate,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField: 'The date the lot loan started.',
    },
    {
      type: 'number',
      name: 'lotDaysOfLoan',
      label: 'Days of loan',
      value: assumptionsInput?.lotDaysOfLoan,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'The dollar amount of the loan meant for the lot.',
    },
  ]

  const constructionLoanFields = [
    {
      name: 'constructionLender',
      value: analysisInput?.constructionLender || '',
      label: 'Lender',
      options: (props?.session?.me?.activeOrg?.lenders || []).filter(
        (lender) => lender.type?.includes('Construction'),
      ),
      optionName: 'lenders',
      removeOption: true,
      type: 'select',
      analysis: true,
      xs: 12,
      md: 6,
    },
    {
      type: 'dollar',
      name: 'b2rConstructionOfferPrice',
      label: 'Loan Amount',
      value: isNil(analysisInput?.b2rConstructionOfferPrice)
        ? ''
        : analysisInput.b2rConstructionOfferPrice?.toString(),
      xs: 12,
      lg: 6,
      analysis: true,
      tooltipField:
        'The dollar amount of the loan meant for the construction.',
    },
    {
      type: 'date',
      name: 'constructionStartDate',
      label: 'Start Date',
      value: assumptionsInput?.constructionStartDate,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField: 'The date the construction loan started.',
    },
  ]

  const mezzanineLoanFields = (index) => [
    {
      name: `mezzanineLender.${index}`,
      value: analysisInput?.mezzanineLender?.[index],
      label: 'Lender',
      options: (props?.session?.me?.activeOrg?.lenders || []).filter(
        (lender) => lender.type?.includes('Mezzanine'),
      ),
      optionName: 'lenders',
      removeOption: true,
      type: 'select',
      analysis: true,
      xs: 12,
      md: 6,
    },
    {
      type: 'dollar',
      name: `b2rMezzanineOfferPrice.${index}`,
      label: 'Loan Amount',
      value: analysisInput?.b2rMezzanineOfferPrice?.[index],
      xs: 12,
      lg: 6,
      disabled: index === 1,
      analysis: true,
      tooltipField:
        'The dollar amount of the loan meant for the additional expenses that may occur.',
    },
    {
      type: 'date',
      name: `mezzanineStartDate.${index}`,
      label: 'Start Date',
      value: assumptionsInput?.mezzanineStartDate?.[index],
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField: 'The date the mezzanine loan started.',
    },
  ]

  const inputFields = [
    {
      type: 'dollar',
      name: 'resellPrice',
      label: 'Resale Price',
      value: isNil(analysisInput?.resellPrice)
        ? ''
        : analysisInput.resellPrice?.toString(),
      xs: 12,
      lg: 6,
      analysis: true,
      tooltipField:
        'The price you will resell the property for once the rehab is completed.',
    },
    {
      type: 'dollar',
      label: 'Cost to Build',
      name: 'build.build.buildToCost',
      value: lotProcessesInput?.build?.build?.buildToCost,
      xs: 12,
      lg: 6,
      lotProcess: true,
      tooltipField: '',
    },
    {
      type: 'percentage',
      name: 'costToBuildBuffer',
      label: 'Cost to Build Buffer',
      value: assumptionsInput?.costToBuildBuffer,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField: '',
    },
    {
      type: 'dropdown',
      name: 'salesCommission',
      label: 'In-House Agent Fee',
      value: assumptionsInput?.salesCommission,
      adornmentToggle: assumptionsInput?.salesCommissionType,
      adornment: assumptionsInput?.salesCommissionType,
      adornmentName: 'salesCommissionType',
      xs: 12,
      lg: 6,
      assumptions: true,
      dropdown: true,
      tooltipField:
        'Sales Commission on the Future Sale of Property. Expressed as a dollar amount or percentage of resale price.',
    },
    {
      type: 'dropdown',
      name: 'sellerClosingCost',
      label: 'Seller Closing Cost',
      value: assumptionsInput?.sellerClosingCost,
      adornmentToggle: assumptionsInput?.sellerClosingCostType,
      adornment: assumptionsInput?.sellerClosingCostType,
      adornmentName: 'sellerClosingCostType',
      xs: 12,
      lg: 6,
      assumptions: true,
      dropdown: true,
      tooltipField:
        'Closing Costs Paid by the Seller. Expressed as a dollar amount or percentage of the resale price',
    },
    {
      type: 'dropdown',
      name: 'dispositionFee',
      label: 'Disposition Fee',
      value: assumptionsInput?.dispositionFee,
      adornmentToggle: assumptionsInput?.dispositionFeeType,
      adornment: assumptionsInput?.dispositionFeeType,
      adornmentName: 'dispositionFeeType',
      xs: 12,
      lg: 6,
      assumptions: true,
      dropdown: true,
      tooltipField:
        'Advanced Fee - Additional marketing fee on top of sales commission. Expressed as a dollar amount or percentage of resale price.',
    },
    {
      type: 'dollar',
      name: 'propertyTaxes',
      label: 'Property Taxes',
      value: isNil(analysisInput?.propertyTaxes)
        ? ''
        : analysisInput.propertyTaxes?.toString(),
      tooltipButtonText: 'Show Property Tax data',
      xs: 12,
      lg: 6,
      analysis: true,
      definition: 'propertyTaxes',
      tooltipField:
        'A tax paid on property owned by an individual or other legal entity, such as a corporation.',
    },
    {
      type: 'dollar',
      name: 'otherExpense',
      label: 'Other Expenses',
      value:
        assumptionsInput?.otherExpense === null
          ? ''
          : assumptionsInput.otherExpense,
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'Any annual expense that has not already been labeled.',
    },
  ]

  const endBuyerFields = [
    {
      dropdown: true,
      type: 'dropdown',
      label: 'HOA',
      name: 'HOA',
      value: analysisInput?.HOA,
      adornment: analysisInput?.HOAType || 'Yearly',
      adornmentToggle: analysisInput?.HOAType,
      adornmentName: 'HOAType',
      xs: 12,
      lg: 6,
      analysis: true,
      tooltipField:
        'An organization in a subdivision, planned community, or condominium building that makes and enforces rules for the properties and its residents. These residents automatically become members and are required to pay dues, known as HOA fees.',
    },
    {
      type: 'dollar',
      name: 'rent.0',
      label: 'Total Rental Income',
      tooltipButtonText: 'Show Total Rent data',
      value: isNil(analysisInput?.rent?.[0])
        ? ''
        : analysisInput.rent?.[0]?.toString(),
      xs: 12,
      lg: 6,
      definition: 'rent',
      analysis: true,
      tooltipField:
        "A tenant's monthly payment to a landlord for the use of property or land.",
    },
    {
      dropdown: true,
      type: 'dropdown',
      name: 'propertyManagement',
      label: 'Property Mgmt Fee',
      value: assumptionsInput?.propertyManagement,
      adornmentToggle: assumptionsInput?.propertyManagementType,
      adornment: assumptionsInput?.propertyManagementType,
      adornmentName: 'propertyManagementType',
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'Property management fee expressed as a dollar amount or percentage of Gross Rental Income (rent - vacancy).',
    },
    {
      dropdown: true,
      type: 'dropdown',
      name: 'propertyInsurance',
      label: 'Annual Insurance',
      value: assumptionsInput?.propertyInsurance,
      adornmentToggle: assumptionsInput?.propertyInsuranceType,
      adornment: assumptionsInput?.propertyInsuranceType,
      adornmentName: 'propertyInsuranceType',
      xs: 12,
      lg: 6,
      assumptions: true,
      tooltipField:
        'Annual property insurance premium expressed as a dollar amount or percentage of the purchase price.',
    },
    {
      type: 'dollar',
      name: 'appraisedValue',
      label: 'Appraised Value',
      value: isNil(lotInput?.appraisedValue)
        ? ''
        : lotInput.appraisedValue?.toString(),
      xs: 12,
      lg: 6,
      lot: true,
      tooltipField:
        'Appraised Value is how much the house is worth right now.',
    },
  ]

  // Provide a robust default analytics object
  const defaultAnalytics = {
    B2R: {
      belowB2RNetProfit: 0,
      capRateB2R: 0,
      lotMPInterestOnlyLoanTotal: 0,
      constructionMPInterestOnlyLoanTotal: 0,
      mezzanineMPInterestOnlyLoanTotal: [],
      lotPointsAmount: 0,
      constructionPointsAmount: 0,
      mezzaninePointsAmount: [],
      salesCommissionTotal: 0,
      dispositionFeeDollarB2R: 0,
      sellingClosingCostDollarB2R: 0,
      // ...add any other fields you know are used in the UI
    },
    // ...add other top-level keys if needed
  }

  // In the hook, use this default:
  const analytics = props.analytics || defaultAnalytics

  const lot = lotInput
  const lotProcesses = lotProcessesInput
  const assumptions = assumptionsInput
  const property = propertyInput
  const analysis = analysisInput

  const totalCostOfFinancing =
    (analytics.B2R.lotMPInterestOnlyLoanTotal || 0) +
    (analytics.B2R.constructionMPInterestOnlyLoanTotal || 0) +
    sum(analytics.B2R.mezzanineMPInterestOnlyLoanTotal) +
    (analytics.B2R.lotPointsAmount || 0) +
    (analytics.B2R.constructionPointsAmount || 0) +
    sum(analytics.B2R.mezzaninePointsAmount)
  const totalExpenses =
    Number(lot.askingPrice) +
    Number(get(lotProcesses, 'build.build.buildToCost') || 0) +
    Number(
      ((get(lotProcesses, 'build.build.buildToCost') || 0) *
        (assumptions.costToBuildBuffer || 0)) /
        100,
    ) +
    Number(assumptions.otherExpense) +
    Number(analytics.B2R.salesCommissionTotal) +
    Number(analytics.B2R.dispositionFeeDollarB2R) +
    Number(analytics.B2R.sellingClosingCostDollarB2R) +
    totalCostOfFinancing
  const netProfit = (analysis.sellPrice || 0) - (totalExpenses || 0)

  const lotTargetFields = [
    {
      label: 'Net Profit',
      value: netProfit > 0 ? netProfit : 0,
      xs: 12,
      sm: 6,
      decimal: false,
    },
    {
      label: 'Cap Rate',
      value:
        analytics.B2R.capRateB2R > 0 ? analytics.B2R.capRateB2R : 0,
      xs: 12,
      sm: 6,
      decimal: true,
    },
    {
      label: 'Total Cost of Financing',
      value: totalCostOfFinancing > 0 ? totalCostOfFinancing : 0,
      xs: 12,
      sm: 6,
      decimal: true,
    },
    {
      label: 'Total Expenses',
      value: totalExpenses > 0 ? totalExpenses : 0,
      xs: 12,
      sm: 6,
      decimal: false,
    },
    {
      label: 'Cashflow',
      value:
        netProfit +
          (analytics.B2R.constructionMPInterestOnlyLoanTotal || 0) >
        0
          ? netProfit +
            (analytics.B2R.constructionMPInterestOnlyLoanTotal || 0)
          : 0,
      xs: 12,
      sm: 6,
      decimal: false,
    },
  ]

  const PerSqftLotTargetFields = [
    {
      label: 'Price to Build per sqft',
      value:
        head(property.sqft) > 0
          ? Number(get(lotProcesses, 'build.build.buildToCost')) +
              Number(
                (get(lotProcesses, 'build.build.buildToCost') *
                  (assumptions.costToBuildBuffer || 0)) /
                  100,
              ) >
            0
            ? (Number(get(lotProcesses, 'build.build.buildToCost')) +
                Number(
                  (get(lotProcesses, 'build.build.buildToCost') *
                    (assumptions.costToBuildBuffer || 0)) /
                    100,
                )) /
              head(property.sqft)
            : 0
          : 0,
      xs: 12,
      sm: 6,
      decimal: true,
    },
    {
      label: 'Rent per sqft',
      value:
        head(property.sqft) > 0
          ? Array.isArray(analysis.rent) && analysis.rent[0] > 0
            ? analysis.rent[0] / head(property.sqft)
            : 0
          : 0,
      xs: 12,
      sm: 6,
      decimal: true,
    },
    {
      label: 'Sale Price per sqft',
      value:
        head(property.sqft) > 0
          ? lot.appraisedValue > 0
            ? lot.appraisedValue / head(property.sqft)
            : 0
          : 0,
      xs: 12,
      sm: 6,
      decimal: true,
    },
  ]

  // Instantiate renderInputFields using the same arguments as in index.jsx
  const renderInputFields = renderInputFieldsFunction(
    {
      onChangeProperty: () => {}, // TODO: wire up or pass from props if needed
      onChangeAnalysis: () => {},
      onChangeAssumptions: () => {},
      onChangeLot: () => {},
      onChangeLotProcess: () => {},
      onChangeExchange: () => {},
    },
    {
      showFormatDropdown: false,
      anchorElAssumptions: null,
      toggleAssumptionsMenuFormat: () => {},
      handleAssumptionsMenuClose: () => {},
      handleAdornmentChange: () => {},
      handleAnalysisAdornmentChange: () => {},
    },
    props?.session?.me?.activeOrg?.member,
  )

  // Return all state, handlers, and field arrays
  return {
    ...props,
    analysisInput,
    setAnalysisInput,
    lotInput,
    setLotInput,
    propertyInput,
    setPropertyInput,
    assumptionsInput,
    setAssumptionsInput,
    lotProcessesInput,
    setLotProcessesInput,
    loader,
    setLoader,
    lotDetailFields,
    lotLoanFields,
    constructionLoanFields,
    mezzanineLoanFields,
    inputFields,
    endBuyerFields,
    lotTargetFields,
    PerSqftLotTargetFields,
    renderInputFields,
    // ...other state and handlers
  }
}

export default useEditLotForm
